import { useState } from 'react';
import * as React from 'react';

import { InfoIcon } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { BAZAK_URL } from '@/common/constants';
import BazakLogo from '@/components/bazak-logo';
import { useTenantKey } from '@/components/providers/config-provider';
import { MarkdownLink } from '@/components/ui/markdown-link';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useLocalizedConfigUrl } from '@/hooks/use-localized-config-url';
import { useWatermarkUrl } from '@/hooks/use-watermark-url';
import { cn } from '@/lib/utils';
import { createDebouncedResizeObserver } from '@/lib/utils/resize-observer';

const DISCLAIMER_GAP = 12;
const POWERED_BY_BUFFER = 16;

export function DisclaimerRow() {
  const t = useTranslations();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const tenantKey = useTenantKey();
  const tenantName = useLocalizedConfig<string>('meta.tenantName') || tenantKey;
  const termsUrl = useLocalizedConfigUrl({
    path: 'navigation.termsUrl',
    fallback: `${BAZAK_URL}/terms-and-conditions`,
  });
  const watermarkUrl = useWatermarkUrl();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const disclaimerRef = React.useRef<HTMLDivElement>(null);
  const poweredByRef = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.getBoundingClientRect().width);
    const { observer, disconnect } = createDebouncedResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return disconnect;
  }, []);

  React.useEffect(() => {
    if (contentWidth !== null || !containerWidth) return;
    const disclaimer = disclaimerRef.current;
    const poweredBy = poweredByRef.current;
    if (!disclaimer || !poweredBy) return;

    setContentWidth(disclaimer.scrollWidth + poweredBy.scrollWidth + DISCLAIMER_GAP);
  }, [contentWidth, containerWidth]);

  const showPoweredByText =
    contentWidth === null || contentWidth + POWERED_BY_BUFFER <= containerWidth;

  return (
    <div
      ref={containerRef}
      className="flex flex-row items-center justify-between gap-3 text-xs text-foreground/60"
    >
      <div ref={disclaimerRef} className="flex min-w-0 items-center gap-1.5">
        <span className="truncate leading-none">{t('disclaimer.text')}</span>
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex cursor-help items-center"
              aria-label={t('disclaimer.tooltipAriaLabel')}
            >
              <InfoIcon className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-[min(320px,calc(100vw-2rem))] whitespace-pre-line"
          >
            {t.rich('disclaimer.tooltip', {
              tenantName,
              termsLink: chunks => (
                <MarkdownLink href={termsUrl} target="_blank" className="font-normal text-inherit">
                  {chunks}
                </MarkdownLink>
              ),
            })}
          </TooltipContent>
        </Tooltip>
      </div>
      <Link
        ref={poweredByRef}
        href={watermarkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex shrink-0 items-center gap-1.5 transition-colors hover:text-foreground"
      >
        <span className={cn('leading-none', !showPoweredByText && 'hidden')}>
          {t('disclaimer.poweredByText')}
        </span>
        <BazakLogo className="h-3 w-auto" />
      </Link>
    </div>
  );
}
